import React, { useMemo, useState } from 'react';
import {
  Accordion,
  Badge,
  Group,
  Loader,
  SegmentedControl,
  Stack,
  Text,
  ThemeIcon,
  Timeline,
} from '@mantine/core';
import {
  IconEdit,
  IconFileDownload,
  IconHistory,
  IconMail,
  IconPlus,
  IconTrash,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import {
  ClinicalNoteType,
  IClinicalNoteActivityEvent,
} from '../../entities/ClinicalNote/ClinicalNote';
import { useClinicalNoteActivityHistory } from '../../entities/ClinicalNote/queries/useClinicalNoteActivityHistory';

interface ClinicalNoteActivityHistoryProps {
  noteType: ClinicalNoteType;
  i18nPrefix: string;
  patientId: string | number | undefined;
}

type FilterValue = 'all' | 'edits' | 'exports';

const ACTION_CONFIG: Record<
  string,
  { icon: React.ElementType; color: string }
> = {
  Created: { icon: IconPlus, color: 'blue' },
  Edited: { icon: IconEdit, color: 'gray' },
  Deleted: { icon: IconTrash, color: 'red' },
  PdfDownloaded: { icon: IconFileDownload, color: 'gray' },
  SentToAssistant: { icon: IconMail, color: 'grape' },
};

function formatRelative(date: Date, lang: string): string {
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.round(diffMs / 60000);
  const rtf = new Intl.RelativeTimeFormat(lang === 'fr' ? 'fr-CA' : 'en-CA', {
    numeric: 'auto',
  });
  if (Math.abs(diffMin) < 60) return rtf.format(-diffMin, 'minute');
  const diffHours = Math.round(diffMin / 60);
  if (Math.abs(diffHours) < 24) return rtf.format(-diffHours, 'hour');
  const diffDays = Math.round(diffHours / 24);
  if (Math.abs(diffDays) < 30) return rtf.format(-diffDays, 'day');
  const diffMonths = Math.round(diffDays / 30);
  return rtf.format(-diffMonths, 'month');
}

function matchesFilter(action: string, filter: FilterValue): boolean {
  switch (filter) {
    case 'all':
      return true;
    case 'edits':
      return (
        action === 'Created' || action === 'Edited' || action === 'Deleted'
      );
    case 'exports':
      return action === 'PdfDownloaded' || action === 'SentToAssistant';
    default:
      return true;
  }
}

function describeEvent(
  event: IClinicalNoteActivityEvent,
  i18nPrefix: string,
  t: (k: string, vars?: Record<string, unknown>) => string,
  i18nLang: string,
): string {
  const details = event.details ?? {};
  const examDateRaw = details.examDate as string | undefined;
  const examDate = examDateRaw
    ? new Date(examDateRaw).toLocaleString(
        i18nLang === 'fr' ? 'fr-CA' : 'en-CA',
        {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        },
      )
    : '';
  switch (event.action) {
    case 'Created':
      return t(`${i18nPrefix}.activity.descriptions.created`, { examDate });
    case 'Deleted':
      return t(`${i18nPrefix}.activity.descriptions.deleted`, { examDate });
    case 'Edited':
      return t(`${i18nPrefix}.activity.descriptions.edited`, { examDate });
    case 'PdfDownloaded':
      return t(`${i18nPrefix}.activity.descriptions.pdfDownloaded`);
    case 'SentToAssistant':
      return t(`${i18nPrefix}.activity.descriptions.sentToAssistant`, {
        name:
          (details.assistantName as string) ||
          (details.assistantEmail as string) ||
          '',
      });
    default:
      return event.action;
  }
}

export const ClinicalNoteActivityHistory: React.FC<
  ClinicalNoteActivityHistoryProps
> = ({ noteType, i18nPrefix, patientId }) => {
  const { t, i18n } = useTranslation();
  const { data: events, isLoading } = useClinicalNoteActivityHistory(
    noteType,
    patientId,
  );
  const [filter, setFilter] = useState<FilterValue>('all');

  const filtered = useMemo(() => {
    if (!events) return [];
    return events.filter((e) => matchesFilter(e.action, filter));
  }, [events, filter]);

  const formatAbsolute = (iso: string): string =>
    new Date(iso).toLocaleString(i18n.language === 'fr' ? 'fr-CA' : 'en-CA', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });

  return (
    <Accordion variant="separated" defaultValue={null}>
      <Accordion.Item value="activity">
        <Accordion.Control icon={<IconHistory size={18} />}>
          <Group gap="xs">
            <Text fw={600}>{t(`${i18nPrefix}.activity.title`)}</Text>
            {events && events.length > 0 && (
              <Badge variant="light" color="gray">
                {events.length}
              </Badge>
            )}
          </Group>
        </Accordion.Control>
        <Accordion.Panel>
          {isLoading ? (
            <Loader size="sm" />
          ) : !events || events.length === 0 ? (
            <Text size="sm" c="dimmed">
              {t(`${i18nPrefix}.activity.empty`)}
            </Text>
          ) : (
            <Stack gap="md">
              <SegmentedControl
                size="xs"
                value={filter}
                onChange={(v) => setFilter(v as FilterValue)}
                data={[
                  {
                    value: 'all',
                    label: t(`${i18nPrefix}.activity.filters.all`),
                  },
                  {
                    value: 'edits',
                    label: t(`${i18nPrefix}.activity.filters.edits`),
                  },
                  {
                    value: 'exports',
                    label: t(`${i18nPrefix}.activity.filters.exports`),
                  },
                ]}
              />
              {filtered.length === 0 ? (
                <Text size="sm" c="dimmed">
                  {t(`${i18nPrefix}.activity.emptyForFilter`)}
                </Text>
              ) : (
                <Timeline bulletSize={22} lineWidth={2} active={-1}>
                  {filtered.map((event) => {
                    const config = ACTION_CONFIG[event.action] ?? {
                      icon: IconHistory,
                      color: 'gray',
                    };
                    const Icon = config.icon;
                    const eventDate = new Date(event.eventDate);
                    return (
                      <Timeline.Item
                        key={event.id}
                        bullet={
                          <ThemeIcon
                            size={22}
                            color={config.color}
                            radius="xl"
                            variant="filled"
                          >
                            <Icon size={12} />
                          </ThemeIcon>
                        }
                        title={
                          <Text size="sm" fw={500}>
                            {describeEvent(event, i18nPrefix, t, i18n.language)}
                          </Text>
                        }
                      >
                        <Text size="xs" c="dimmed">
                          {event.actorName ? `${event.actorName} · ` : ''}
                          {formatAbsolute(event.eventDate)}
                          {' · '}
                          {formatRelative(eventDate, i18n.language)}
                        </Text>
                      </Timeline.Item>
                    );
                  })}
                </Timeline>
              )}
            </Stack>
          )}
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );
};
